import { ExternalLinkIcon } from 'assets/icons';
import { INVOICE_DISCOUNT_CALCULATION_LEARN_MORE_URL } from 'config/index';
import { selectUserDetails } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { FC, KeyboardEvent, useEffect, useState } from 'react';
import { FirebaseStorage } from 'services/firebase';
import { FireBaseCollections } from 'utils/firebase';

/** Firestore Merchant doc field — invoice settings → Discount section */
export const DISCOUNT_CALCULATION_ANNOUNCEMENT_FIELD_INVOICE_SETTINGS =
  FireBaseCollections.Merchants.fields.seenInvoiceDiscountCalculationCoachmarkSettingsWeb;

/** Firestore Merchant doc field — create invoice → line items discount row */
export const DISCOUNT_CALCULATION_ANNOUNCEMENT_FIELD_CREATE_INVOICE =
  FireBaseCollections.Merchants.fields.seenInvoiceDiscountCalculationCoachmarkCreateInvoiceWeb;

/** Firestore Merchant doc field — add / edit discount modal → apply-after-tax coachmark */
export const DISCOUNT_CALCULATION_ANNOUNCEMENT_FIELD_ADD_DISCOUNT_MODAL =
  FireBaseCollections.Merchants.fields.seenInvoiceDiscountCalculationCoachmarkAddDiscountModalWeb;

/** `modal` keeps the popover visible (no md breakpoint) for narrow modal layouts. */
export type DiscountAnnouncementPlacementScope = 'default' | 'modal';

type DiscountCalculationFeatureAnnouncementProps = {
  dismissFirestoreField: string;
  className?: string;
  placementScope?: DiscountAnnouncementPlacementScope;
  titleOverride?: string;
  descriptionOverride?: string;
  /** @default true */
  showLearnMoreLink?: boolean;
};

const DiscountCalculationFeatureAnnouncement: FC<DiscountCalculationFeatureAnnouncementProps> = ({
  dismissFirestoreField,
  className = '',
  placementScope = 'default',
  titleOverride,
  descriptionOverride,
  showLearnMoreLink = true
}) => {
  const merchantId = useAppSelector(selectUserDetails)?.id;
  const [isDismissed, setIsDismissed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!merchantId) {
      return;
    }
    let isCancelled = false;
    const loadDismissedFlag = async () => {
      const hasSeen = await FirebaseStorage.getMerchantCollectionFieldValue({
        merchantId,
        collectionFieldName: dismissFirestoreField
      });
      if (!isCancelled) {
        setIsDismissed(Boolean(hasSeen));
      }
    };
    void loadDismissedFlag();
    return () => {
      isCancelled = true;
    };
  }, [merchantId, dismissFirestoreField]);

  const handleDismiss = () => {
    setIsDismissed(true);
    if (!merchantId) {
      return;
    }
    void FirebaseStorage.updateMerchantCollectionFieldValue({
      merchantId,
      collectionFieldName: dismissFirestoreField,
      value: true
    });
  };

  const handleDismissKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleDismiss();
    }
  };

  if (isDismissed === null || isDismissed) {
    return null;
  }

  const isModalScope = placementScope === 'modal';

  /** Modal scope: show card to the right of the toggle anchor. */
  const modalRootClass =
    'pointer-events-auto absolute left-full top-1/2 z-[70] ml-2 max-w-[272px] ' +
    'w-[min(272px,calc(100vw-4rem))] -translate-y-1/2';
  const pageRootClass =
    'relative z-[2] w-full max-w-[min(100%,320px)] md:absolute md:left-full md:top-1/2 md:ml-2.5 ' +
    'md:max-w-[300px] md:w-[min(300px,calc(100vw-2rem))] md:-translate-y-1/2';
  const rootClass = isModalScope ? modalRootClass : pageRootClass;

  const arrowClass =
    'absolute top-1/2 left-0 z-[1] h-0 w-0 -translate-x-full -translate-y-1/2 ' +
    'border-y-[7px] border-y-transparent border-r-[9px] border-r-[#252a32]';
  const arrowVisibilityClass = isModalScope ? '' : 'hidden md:block';

  const cardClass = 'rounded-lg bg-[#252a32] px-4 py-3 shadow-[0px_8px_24px_rgba(9,26,70,0.35)]';

  const titleText = titleOverride ?? 'New!';
  const descriptionText =
    descriptionOverride ?? 'You can now control whether discounts apply before or after tax calculations.';
  const descriptionClass = 'mt-2 text-px13 font-normal leading-snug text-white';
  const actionBlueTextClass = 'text-[#5A9EDC]';

  const regionLabel = titleOverride ?? 'New invoice discount options';

  const cardEl = (
    <div className={`relative z-[1] ${cardClass}`}>
      <div className="text-sbase font-bold text-white">{titleText}</div>
      <p className={descriptionClass}>{descriptionText}</p>

      <div className={`mt-2 flex items-center ${showLearnMoreLink ? 'justify-between' : 'justify-end'}`}>
        {showLearnMoreLink ? (
          <a
            href={INVOICE_DISCOUNT_CALCULATION_LEARN_MORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            tabIndex={0}
            className={`inline-flex items-center gap-1.5 text-sm font-semibold hover:underline ${actionBlueTextClass}`}
            aria-label="Learn more about discount calculation (opens in a new tab)">
            Learn more
            <ExternalLinkIcon className={`path-fill-current h-4 w-4 shrink-0 ${actionBlueTextClass}`} aria-hidden />
          </a>
        ) : null}
        <button
          type="button"
          className={`text-sbase font-semibold hover:underline ${actionBlueTextClass}`}
          onClick={handleDismiss}
          onKeyDown={handleDismissKeyDown}
          aria-label="Dismiss discount announcement">
          Okay
        </button>
      </div>
    </div>
  );

  return (
    <div className={`${rootClass} ${className}`} role="region" aria-label={regionLabel}>
      <div className={`${arrowClass} ${arrowVisibilityClass}`} aria-hidden />
      {cardEl}
    </div>
  );
};

export default DiscountCalculationFeatureAnnouncement;
